import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
} from '@angular/core';
import { AppSvgIconComponent } from '@vallift/ngl';
import { isLayoutFieldType } from '../../utils/create-field-from-catalog';
import type { FormField } from '../../models/built-form';
import { HeadingConfigViewComponent } from './views/heading-config-view/heading-config-view';
import { ParagraphConfigViewComponent } from './views/paragraph-config-view/paragraph-config-view';
import { DividerConfigViewComponent } from './views/divider-config-view/divider-config-view';
import { SpacerConfigViewComponent } from './views/spacer-config-view/spacer-config-view';
import { TextConfigViewComponent } from './views/text-config-view/text-config-view';
import { TextareaConfigViewComponent } from './views/textarea-config-view/textarea-config-view';
import { EmailConfigViewComponent } from './views/email-config-view/email-config-view';
import { PhoneConfigViewComponent } from './views/phone-config-view/phone-config-view';
import { NumberConfigViewComponent } from './views/number-config-view/number-config-view';
import { CurrencyConfigViewComponent } from './views/currency-config-view/currency-config-view';
import { UrlConfigViewComponent } from './views/url-config-view/url-config-view';
import { CheckboxConfigViewComponent } from './views/checkbox-config-view/checkbox-config-view';
import { CheckboxGroupConfigViewComponent } from './views/checkbox-group-config-view/checkbox-group-config-view';
import { RadioGroupConfigViewComponent } from './views/radio-group-config-view/radio-group-config-view';
import { SwitchConfigViewComponent } from './views/switch-config-view/switch-config-view';
import { SelectConfigViewComponent } from './views/select-config-view/select-config-view';
import { SearchablePickerConfigViewComponent } from './views/searchable-picker-config-view/searchable-picker-config-view';
import { SingleChoiceConfigViewComponent } from './views/single-choice-config-view/single-choice-config-view';
import { MultiChoiceConfigViewComponent } from './views/multi-choice-config-view/multi-choice-config-view';
import { DateConfigViewComponent } from './views/date-config-view/date-config-view';
import { TimeConfigViewComponent } from './views/time-config-view/time-config-view';
import { DatetimeConfigViewComponent } from './views/datetime-config-view/datetime-config-view';
import { DateRangeConfigViewComponent } from './views/date-range-config-view/date-range-config-view';
import { TimeRangeConfigViewComponent } from './views/time-range-config-view/time-range-config-view';
import { SliderConfigViewComponent } from './views/slider-config-view/slider-config-view';
import { RatingConfigViewComponent } from './views/rating-config-view/rating-config-view';
import { RangeSliderConfigViewComponent } from './views/range-slider-config-view/range-slider-config-view';
import { ColorConfigViewComponent } from './views/color-config-view/color-config-view';
import { NumericStepperConfigViewComponent } from './views/numeric-stepper-config-view/numeric-stepper-config-view';
import { ImageConfigViewComponent } from './views/image-config-view/image-config-view';
import { CameraConfigViewComponent } from './views/camera-config-view/camera-config-view';
import { LiveGeoCoordinatesConfigViewComponent } from './views/live-geo-coordinates-config-view/live-geo-coordinates-config-view';
import { AddressSelectorConfigViewComponent } from './views/address-selector-config-view/address-selector-config-view';
import { MapConfigViewComponent } from './views/map-config-view/map-config-view';
import { FileUploaderConfigViewComponent } from './views/file-uploader-config-view/file-uploader-config-view';
import { AudioRecorderConfigViewComponent } from './views/audio-recorder-config-view/audio-recorder-config-view';
import { VideoRecorderConfigViewComponent } from './views/video-recorder-config-view/video-recorder-config-view';
import { RichTextConfigViewComponent } from './views/rich-text-config-view/rich-text-config-view';
import { DocumentScannerConfigViewComponent } from './views/document-scanner-config-view/document-scanner-config-view';
import { DrawingConfigViewComponent } from './views/drawing-config-view/drawing-config-view';
import { SignatureConfigViewComponent } from './views/signature-config-view/signature-config-view';
import { CaptchaConfigViewComponent } from './views/captcha-config-view/captcha-config-view';
import {
  MultiChoiceGridConfigViewComponent
} from './views/multi-choice-grid-config-view/multi-choice-grid-config-view';
import { SingleChoiceGridConfigViewComponent } from './views/single-choice-grid-config-view/single-choice-grid-config-view';
import { NumberGridConfigViewComponent } from './views/number-grid-config-view/number-grid-config-view';

@Component({
  selector: 'app-field-config-panel',
  standalone: true,
  imports: [
    AppSvgIconComponent,
    HeadingConfigViewComponent,
    ParagraphConfigViewComponent,
    DividerConfigViewComponent,
    SpacerConfigViewComponent,
    TextConfigViewComponent,
    TextareaConfigViewComponent,
    EmailConfigViewComponent,
    PhoneConfigViewComponent,
    NumberConfigViewComponent,
    CurrencyConfigViewComponent,
    UrlConfigViewComponent,
    CheckboxConfigViewComponent,
    CheckboxGroupConfigViewComponent,
    RadioGroupConfigViewComponent,
    SwitchConfigViewComponent,
    SelectConfigViewComponent,
    SearchablePickerConfigViewComponent,
    SingleChoiceConfigViewComponent,
    MultiChoiceConfigViewComponent,
    DateConfigViewComponent,
    TimeConfigViewComponent,
    DatetimeConfigViewComponent,
    DateRangeConfigViewComponent,
    TimeRangeConfigViewComponent,
    SliderConfigViewComponent,
    RatingConfigViewComponent,
    RangeSliderConfigViewComponent,
    ColorConfigViewComponent,
    NumericStepperConfigViewComponent,
    ImageConfigViewComponent,
    CameraConfigViewComponent,
    LiveGeoCoordinatesConfigViewComponent,
    AddressSelectorConfigViewComponent,
    MapConfigViewComponent,
    FileUploaderConfigViewComponent,
    AudioRecorderConfigViewComponent,
    VideoRecorderConfigViewComponent,
    RichTextConfigViewComponent,
    DocumentScannerConfigViewComponent,
    DrawingConfigViewComponent,
    SignatureConfigViewComponent,
    CaptchaConfigViewComponent,
    MultiChoiceGridConfigViewComponent,
    SingleChoiceGridConfigViewComponent,
    NumberGridConfigViewComponent,
  ],
  templateUrl: './field-config-panel.html',
  styleUrl: './field-config-panel.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FieldConfigPanelComponent {
  readonly field = input<FormField | null>(null);
  readonly fieldChange = output<FormField>();
  readonly remove = output<void>();
  readonly duplicate = output<void>();

  readonly isLayoutField = computed(() => {
    const type = this.field()?.type;
    return type ? isLayoutFieldType(type) : false;
  });
}
